import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Tooltip } from '@mui/material';

import AddLampToolbar from '../../../assets/toolbar_icons/AddLampToolbar.svg?react';
import CursorToolbar from '../../../assets/toolbar_icons/CursorToolbar.svg?react';
import ImportLampsToolbar from '../../../assets/toolbar_icons/ImportLampsToolbar.svg?react';
import LampDistanceToolbar from '../../../assets/toolbar_icons/LampDistanceToolbar.svg?react';
import LineMeasurmentToolbar from '../../../assets/toolbar_icons/LineMeasurmentToolbar.svg?react';
import MeasurementGridToolbar from '../../../assets/toolbar_icons/MeasurementGridToolbar.svg?react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useStore } from '../../../pages/workspace/hooks';
import { setMeasurementGridSpacing } from '../../../store/reducers/workspace.reducer';
import { getUser } from '../../../store/selectors/user.selector.ts';
import {
  isAutopositioningInProgress as checkIsAutopositioningInProgress,
  isCalculationStarted as checkIsCalculationStarted,
} from '../../../utils/jobStatus';
import { shouldUseFrontendMeasurementSource } from '../../../utils/measurementCalculation/measurementSource';
import { requestFrontendMeasurementCalculation } from '../../../utils/measurementCalculation/trigger';
import { formatLength, formatPoint, getLengthUnitLabel } from '../../../utils/unitSystem.ts';
import { CheatSheet } from '../CheatSheet';
import { LuminaireImportExportModal } from './LuminaireImportExportModal';

/** Measurement-grid spacing options (metres) for the frontend calculation. */
const GRID_SPACING_OPTIONS: { value: number; label: string }[] = [
  { value: 0.25, label: '0.25 m' },
  { value: 0.5, label: '0.5 m' },
  { value: 1, label: '1.0 m' },
];

interface ViewModes {
  object: boolean;
  measurement: boolean;
  render: boolean;
  realTimeRender: boolean;
}

interface LeftToolbarProps {
  showExportsPanel: boolean;
  activeViewModes: ViewModes;
}

export function LeftToolbar({ showExportsPanel, activeViewModes }: LeftToolbarProps) {
  /** Lamp/luminaire and screenshot tools only make sense in the scene/object editing view. */
  const showLampTools = activeViewModes.object;
  const { t } = useTranslation();
  const [showImportExportModal, setShowImportExportModal] = useState(false);
  const [showCheatSheet, setShowCheatSheet] = useState(false);
  const [rightToolbarSelected, setRightToolbarSelected] = useState<string | null>(null);
  const user = useSelector(getUser)!;
  const unitSystem = user.settings.units;
  const lengthUnitLabel = getLengthUnitLabel(unitSystem);

  const dispatch = useAppDispatch();
  const userFeatureFlags = useAppSelector((s) => s.featureFlags.userFeatureFlags);
  const measurementGridSpacing = useAppSelector((s) => s.workspace.measurementGridSpacing);
  const [showSpacingMenu, setShowSpacingMenu] = useState(false);

  const {
    lamps,
    addLamp,
    addLamps,
    activeVersion,
    desiredLuminaireHeight,
    setTargetCameraPosition,
    // Measurement tool
    isMeasurementMode,
    measurementPoints,
    measuredDistance,
    activeToolbarTool,
    setActiveToolbarTool,
    clearMeasurementPoints,
    // Lamp-to-Lamp distance tool
    isLampDistanceMode,
    lampDistanceSelectedLamps,
    lampDistancePoints,
    lampDistanceMeasured,
    clearLampDistanceSelection,
  } = useStore();

  // Determine optimization state
  const isCalculationStarted = checkIsCalculationStarted(activeVersion);
  const isAutopositioningInProgress = checkIsAutopositioningInProgress(activeVersion);
  const isCalculationNotStarted = !isCalculationStarted;
  const isMaxLampsReached = lamps.length >= 50;

  // Frontend calculation is flag-gated, except when ALL lamps are catalogue-assigned,
  // in which case the frontend engine is the only available measurement source.
  const frontendCalcEnabled = shouldUseFrontendMeasurementSource(userFeatureFlags, activeVersion);

  // Handler to instantly add a lamp at (0, 0, 0) and move camera to view it
  const handleInstantAddLamp = () => {
    // Clear any active measurement tools
    if (activeToolbarTool === 'measurement') {
      clearMeasurementPoints();
      setActiveToolbarTool('selection');
    }
    if (activeToolbarTool === 'lampDistance') {
      clearLampDistanceSelection();
      setActiveToolbarTool('selection');
    }

    // Immediately add a lamp at (0, 0, 0) without showing the modal
    if (isMaxLampsReached) return;

    const newLamp = {
      name: `Luminaire ${lamps.length + 1}`,
      position: [0, 0, 0] as [number, number, number],
      distanceToGround: desiredLuminaireHeight,
    };
    addLamp(newLamp);

    // Move camera to (0, 0, 20) looking at the new lamp
    setTargetCameraPosition({
      position: [0, 0, 20],
      lookAt: [0, 0, 0],
    });
  };

  // Close modals on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showImportExportModal) {
          setShowImportExportModal(false);
        }
        if (showCheatSheet) {
          setShowCheatSheet(false);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showImportExportModal, showCheatSheet]);

  if (showExportsPanel) {
    return null;
  }

  return (
    <>
      <div className="fixed left-[22px] top-1/2 -translate-y-1/2 flex flex-col items-center bg-[#1E1E1E] border border-white/10 rounded-xl p-1.5 w-[56px] shadow-2xl z-50">
        {/* Selection Tool */}
        <Tooltip title={t('workspace.toolbar.selectionTool')} placement="right">
          <button
            onClick={() => setActiveToolbarTool('selection')}
            className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors mb-1 ${
              activeToolbarTool === 'selection'
                ? 'bg-[#1a1a1a] text-[#d4ff33]'
                : 'bg-transparent text-white hover:bg-white/5 hover:text-gray-300'
            }`}
          >
            <CursorToolbar className="w-6 h-6" />
          </button>
        </Tooltip>

        {/* Measurement grid spacing — only in the measurement (2nd) view + per-user flag */}
        {frontendCalcEnabled && activeViewModes.measurement && (
          <div className="relative flex items-center">
            <Tooltip
              title={
                showSpacingMenu ? null : t('workspace.toolbar.measurementGridSpacing', 'Measurement point spacing')
              }
              placement="right"
            >
              <button
                onClick={() => setShowSpacingMenu((open) => !open)}
                className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors mb-1 ${
                  showSpacingMenu
                    ? 'bg-[#1a1a1a] text-[#d4ff33]'
                    : 'bg-transparent text-white hover:bg-white/5 hover:text-gray-300'
                }`}
              >
                <MeasurementGridToolbar className="w-6 h-6" />
              </button>
            </Tooltip>

            {showSpacingMenu && (
              <div className="absolute left-full ml-4 top-1/2 -translate-y-1/2 bg-[#1E1E1E] border border-white/10 rounded-lg p-2 min-w-[170px] shadow-xl z-50">
                <div className="text-xs text-gray-300 mb-1 px-1">
                  {t('workspace.toolbar.measurementGridSpacing', 'Measurement point spacing')}
                </div>
                {GRID_SPACING_OPTIONS.map((option) => {
                  const active = Math.abs(measurementGridSpacing - option.value) < 1e-9;
                  return (
                    <button
                      key={option.value}
                      onClick={() => {
                        console.info('[grid-spacing] selected', option.value, 'm (was', measurementGridSpacing, 'm)');
                        dispatch(setMeasurementGridSpacing(option.value));
                        requestFrontendMeasurementCalculation();
                        setShowSpacingMenu(false);
                      }}
                      className={`w-full text-left text-sm px-2 py-1.5 rounded-md transition-colors ${
                        active ? 'bg-[#1a1a1a] text-[#d4ff33]' : 'text-white hover:bg-white/5'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Distance Measurement Tool — only in scene/object edit view */}
        {showLampTools && (
          <div className="relative flex items-center group">
            <Tooltip title={isMeasurementMode ? null : t('workspace.toolbar.measurementTool')} placement="right">
              <button
                onClick={() => {
                  if (activeToolbarTool === 'measurement') {
                    clearMeasurementPoints();
                    setActiveToolbarTool('selection');
                  } else {
                    setActiveToolbarTool('measurement');
                  }
                }}
                className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors ${
                  activeToolbarTool === 'measurement'
                    ? 'bg-[#1a1a1a] text-[#d4ff33]'
                    : 'bg-transparent text-white hover:bg-white/5 hover:text-gray-300'
                }`}
              >
                <LineMeasurmentToolbar className="w-6 h-6" />
              </button>
            </Tooltip>

            {isMeasurementMode && (
              <div
                className="absolute left-full ml-4 top-0 bg-[#1E1E1E] border border-white/10 rounded-lg p-3 min-w-[220px] shadow-xl z-50 flex flex-col"
                style={{ maxHeight: '600px' }}
                onMouseEnter={(e) => e.stopPropagation()}
              >
                <div className="text-xs text-gray-300 mb-1 shrink-0">{t('workspace.measurement.measurementMode')}</div>

                {measurementPoints.length === 0 && (
                  <div className="text-xs text-white/60 shrink-0">{t('workspace.measurement.clickFirstPoint')}</div>
                )}

                {measurementPoints.length >= 1 && (
                  <div className="space-y-1 mb-2 overflow-y-auto custom-scrollbar flex-1 min-h-0">
                    {measurementPoints.map((pt, i) => (
                      <div key={i} className="text-xs text-white/60">
                        <span className="text-gray-300">P{i + 1}:</span>{' '}
                        <span className="text-white font-mono text-[10px]">{formatPoint(pt, unitSystem)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {measurementPoints.length >= 1 && !measuredDistance && (
                  <div className="text-xs text-white/60 shrink-0">{t('workspace.measurement.clickSecondPoint')}</div>
                )}

                {measuredDistance !== null && (
                  <>
                    <div className="text-xs text-white/60 mb-1 shrink-0">
                      {t('workspace.measurement.clickSecondPoint')}
                    </div>
                    <div className="text-sm text-[#A3E635] font-medium shrink-0">
                      {t('workspace.measurement.distance')} {formatLength(measuredDistance, unitSystem)}{' '}
                      {lengthUnitLabel}
                    </div>
                  </>
                )}

                {measurementPoints.length > 0 && (
                  <button
                    onClick={() => clearMeasurementPoints()}
                    className="text-xs text-gray-300 hover:text-white mt-2 underline shrink-0"
                  >
                    {t('workspace.measurement.clearPoints')} (Esc)
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Lamp Distance Tool — only in scene/object edit view */}
        {showLampTools && (
          <div className="relative flex items-center">
            <Tooltip title={isLampDistanceMode ? null : t('workspace.toolbar.lampDistanceTool')} placement="right">
              <button
                onClick={() => {
                  if (activeToolbarTool === 'lampDistance') {
                    clearLampDistanceSelection();
                    setActiveToolbarTool('selection');
                  } else {
                    if (activeToolbarTool === 'measurement') {
                      clearMeasurementPoints();
                    }
                    setActiveToolbarTool('lampDistance');
                  }
                }}
                className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors mb-1 ${
                  activeToolbarTool === 'lampDistance'
                    ? 'bg-[#1a1a1a] text-[#d4ff33]'
                    : 'bg-transparent text-white hover:bg-white/5 hover:text-gray-300'
                }`}
              >
                <LampDistanceToolbar className="w-6 h-6" />
              </button>
            </Tooltip>

            {isLampDistanceMode && (
              <div
                className="absolute left-full ml-4 top-1/2 -translate-y-1/2 bg-[#1E1E1E] border border-white/10 rounded-lg p-3 min-w-[220px] shadow-xl z-50"
                onMouseEnter={(e) => e.stopPropagation()}
              >
                <div className="text-xs text-gray-300 mb-1">{t('workspace.measurement.lampDistanceMode')}</div>

                {lampDistanceSelectedLamps.length === 0 && (
                  <div className="text-xs text-white/60">{t('workspace.measurement.selectFirstLamp')}</div>
                )}

                {lampDistanceSelectedLamps.length === 1 && (
                  <>
                    <div className="text-xs text-white/60 mb-2">
                      <span className="text-gray-300">{t('workspace.measurement.selectedLamp')}</span>{' '}
                      <span className="text-white">
                        {lamps.find((l) => l.id === lampDistanceSelectedLamps[0])?.name || 'Lamp 1'}
                      </span>
                    </div>
                    {lampDistancePoints.length > 0 && (
                      <div className="text-xs text-white/60 mb-2">
                        <span className="text-gray-300">{t('workspace.measurement.point1')}</span>{' '}
                        <span className="text-white font-mono">{formatPoint(lampDistancePoints[0], unitSystem)}</span>
                      </div>
                    )}
                    <div className="text-xs text-white/60">{t('workspace.measurement.selectSecondLamp')}</div>
                  </>
                )}

                {lampDistanceSelectedLamps.length === 2 && lampDistancePoints.length === 2 && (
                  <div className="space-y-1 mb-2">
                    <div className="text-xs text-white/60">
                      <span className="text-gray-300">{t('workspace.measurement.selectedLamp')}</span>{' '}
                      <span className="text-white">
                        {lamps.find((l) => l.id === lampDistanceSelectedLamps[0])?.name || 'Lamp 1'}
                      </span>
                    </div>
                    <div className="text-xs text-white/60">
                      <span className="text-gray-300">{t('workspace.measurement.point1')}</span>{' '}
                      <span className="text-white font-mono">{formatPoint(lampDistancePoints[0], unitSystem)}</span>
                    </div>
                    <div className="text-xs text-white/60 mt-2">
                      <span className="text-gray-300">{t('workspace.measurement.selectedLamp')}</span>{' '}
                      <span className="text-white">
                        {lamps.find((l) => l.id === lampDistanceSelectedLamps[1])?.name || 'Lamp 2'}
                      </span>
                    </div>
                    <div className="text-xs text-white/60">
                      <span className="text-gray-300">{t('workspace.measurement.point2')}</span>{' '}
                      <span className="text-white font-mono">{formatPoint(lampDistancePoints[1], unitSystem)}</span>
                    </div>
                  </div>
                )}

                {lampDistanceMeasured !== null && (
                  <div className="text-sm text-[#A3E635] font-medium">
                    {t('workspace.measurement.distance')} {formatLength(lampDistanceMeasured, unitSystem)}{' '}
                    {lengthUnitLabel}
                  </div>
                )}

                {lampDistanceSelectedLamps.length > 0 && (
                  <button
                    onClick={() => clearLampDistanceSelection()}
                    className="text-xs text-gray-300 hover:text-white mt-2 underline"
                  >
                    {t('workspace.measurement.clearSelection')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {showLampTools && (
          <>
            <div className="w-8 h-[1px] bg-white/10 my-2"></div>

            {/* Add Luminaire */}
            <Tooltip
              title={isMaxLampsReached ? t('workspace.addLuminaireManuallyDisabled') : t('workspace.addLuminaire')}
              placement="right"
            >
              <button
                onClick={handleInstantAddLamp}
                disabled={!isCalculationNotStarted || isAutopositioningInProgress || isMaxLampsReached}
                className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors mb-1 ${
                  rightToolbarSelected === 'add-lamp'
                    ? 'bg-[#1a1a1a] text-[#d4ff33]'
                    : 'bg-transparent text-white hover:bg-white/5 hover:text-gray-300'
                } ${
                  !isCalculationNotStarted || isAutopositioningInProgress || isMaxLampsReached
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                <AddLampToolbar className="w-6 h-6" />
              </button>
            </Tooltip>

            {/* Import/Export Luminaires */}
            <Tooltip title={t('workspace.importExportLuminaires')} placement="right">
              <button
                onClick={() => {
                  if (activeToolbarTool === 'measurement') {
                    clearMeasurementPoints();
                    setActiveToolbarTool('selection');
                  }
                  if (activeToolbarTool === 'lampDistance') {
                    clearLampDistanceSelection();
                    setActiveToolbarTool('selection');
                  }
                  setRightToolbarSelected(rightToolbarSelected === 'import-export' ? null : 'import-export');
                  setShowImportExportModal(true);
                }}
                disabled={isAutopositioningInProgress}
                className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors mb-1 bg-transparent text-white hover:bg-white/5 hover:text-gray-300 ${
                  isAutopositioningInProgress ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <ImportLampsToolbar className="w-6 h-6" />
              </button>
            </Tooltip>

            {/* Screenshot/Camera Tool - Coming Soon */}
            <Tooltip title={`${t('workspace.toolbar.screenshotTool')} (${t('common.comingSoon')})`} placement="right">
              <button
                disabled
                className="w-11 h-11 flex items-center justify-center rounded-lg bg-transparent text-gray-500 cursor-not-allowed transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </button>
            </Tooltip>
          </>
        )}

        <div className="w-8 h-[1px] bg-white/10 my-2"></div>

        {/* Help / Cheat Sheet */}
        <Tooltip title={t('workspace.cheatSheet.title')} placement="right">
          <button
            onClick={() => setShowCheatSheet(!showCheatSheet)}
            className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors ${
              showCheatSheet
                ? 'bg-[#1a1a1a] text-[#d4ff33]'
                : 'bg-transparent text-white hover:bg-white/5 hover:text-gray-300'
            }`}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </button>
        </Tooltip>
      </div>

      {/* Import/Export Modal */}
      {showImportExportModal && (
        <LuminaireImportExportModal
          onClose={() => setShowImportExportModal(false)}
          onImport={(importedLamps) => {
            addLamps(importedLamps);
          }}
          lamps={lamps}
          importEnabled={isCalculationNotStarted}
        />
      )}

      {/* Cheat Sheet Overlay */}
      {showCheatSheet && <CheatSheet onClose={() => setShowCheatSheet(false)} />}
    </>
  );
}
